import {createGoogleMapOverlayClass} from "./GoogleMapOverlayFactory.js";
import { resolveControlPosition, resolveMapType } from "./providerConstants.js";

// Google-Cloud-Console "Map ID" (verknüpft den dort hinterlegten Karten-Style; für AdvancedMarkerElement nötig).
// Nur Fallback — der Wert soll von außen kommen können (Page-Config aus PHP / env / Konfig-Datei) und wird
// über options.mapId injiziert. Hier zentral, damit der Hardcode nicht in der Map-Erzeugung versteckt liegt.
const DEFAULT_GOOGLE_MAP_ID = "fe5b3fec33f8c43e";

export class GoogleMapsAdapter {
    _googleApi = null;
    _mapInstance = null;
    _markers = null;

    _overlayClass = null;
    _overlayInstance = null;

    /**
     * @param {object} googleApiObject - das globale 'google' Objekt
     */
    constructor(googleApiObject) {
        if(!googleApiObject || !googleApiObject.maps) {
            throw new Error("Google Maps API object not provided or invalid.");
        }
        this._googleApi = googleApiObject;
        this._markers = new Map();
    }

    // Overlays
    setOverlayClass(googleApiObject) {
        this._overlayClass = createGoogleMapOverlayClass(googleApiObject);
    }

    getOverlayInstance() {
        if(!this._overlayInstance && this._overlayClass) {
            this._overlayInstance = new this._overlayClass(this.getMapInstance());
        }
        return this._overlayInstance;
    }

    removeOverlay() {
        if(this._overlayInstance) {
            this._overlayInstance.remove();
            this._overlayInstance = null;
        }
    }

    /**
     * Initialisiert die Google Map Instanz
     * @param {string} domElementId - ID des Container-Elements
     * @param {object} options - Kartenoptionen
     */
    initializeMap(domElementId, options) {
        const element = document.getElementById(domElementId);
        if (!element) {
            console.error(`Map container with ID "${domElementId}"not found.`);
            return;
        }

        // Einheitlicher Map-Container-Haken: markiert JEDE Map-Instanz (Front main/gallery, Locate-Popup
        // documents, preferences, overlays), damit topo2-Overrides NUR die map-interne Darstellung treffen
        // können (z.B. Button-Shadow, damit die Buttons sich von Markern darunter abheben).
        element.classList.add('topo2-map');

        const gmapOptions = {
            center: options.center || {lat: 0, lng: 0},
            zoom: options.zoom || 8,
            mapTypeId: options.mapTypeId || resolveMapType(this._googleApi, 'roadmap'),
            // hardcoded optionen aus frontendMap.js
            gestureHandling: options.gestureHandling,
            zoomControl: false,
            mapTypeControl: true,
            streetViewControl: false,
            panControl: true,
            rotateControl: true,
            fullscreenControl: false,
            disableDefaultUI: true,
            mapId: options.mapId ?? DEFAULT_GOOGLE_MAP_ID
        };

        try {
            this._mapInstance = new this._googleApi.maps.Map(element, gmapOptions);
            console.debug("[GoogleMapsAdapter] Map instance created via Adapter: ", this._mapInstance);
        } catch(e) {
            console.error('Error creating map instance in Adapter: ', e);
            this._mapInstance = null; // Sicherstellen, dass Instanz null ist bei Fehler
        }
    }

    // (createWmtsImageMapType / getWMTSImageMapType / setWMTSMapOverlay sind entfallen: die WMTS-Kartenwerke
    // stehen jetzt in overlay/wmtsLayers.js und erscheinen als Einträge im OV-Panel, statt als eigenes weißes
    // Checkbox-Control an der Karte zu hängen.)

    /**
     * Gibt die interne Google Maps Instanz zurück (für Übergangszwecke)
     * @returns {google.maps.Map | null}
     */
    getMapInstance() {
        return this._mapInstance;
    }

    /**
     * Liefert das DOM-Element der Karte (kapselt google-spezifisches map.getDiv()).
     * @returns {HTMLElement}
     */
    getMapDiv() {
        return this.getMapInstance().getDiv();
    }

    /**
     * Existiert eine Karteninstanz und hängt ihr Container im Dokument?
     * @returns {boolean}
     */
    isMapAttached() {
        const map = this.getMapInstance();
        return !!map && document.body.contains(map.getDiv());
    }

    /**
     * Setzt Kartenoptionen (kapselt google-spezifisches map.setOptions()).
     * @param {object} options
     */
    setMapOptions(options) {
        const map = this.getMapInstance();
        if (map) {
            map.setOptions(options);
        }
    }

    getZoom() {
        return this.getMapInstance().getZoom();
    }

    getCenter() {
        return this.getMapInstance().getCenter();
    }

    getMapTypeId() {
        return this.getMapInstance().getMapTypeId();
    }

    setZoom(value) {
        this.getMapInstance().setZoom(value);
    }

    setCenter(value) {
        this.getMapInstance().setCenter(value);
    }

    triggerResize() {
        this.trigger(this.getMapInstance(), 'resize');
    }

    fitBounds(bounds) {
        this.getMapInstance().fitBounds(bounds);
    }

    /** Listener **/

    addListener(mapInstance, eventName, callback) {
        return this._googleApi.maps.event.addListener(mapInstance, eventName, callback);
    }

    addListenerOnce(mapInstance, eventName, callback) {
        return this._googleApi.maps.event.addListenerOnce(mapInstance, eventName, callback);
    }

    removeListener(listener) {
        this._googleApi.maps.event.removeListener(listener);
    }

    clearListeners(target, eventName) {
        // ToDo: calls ohne eventName behandeln
        if(!eventName) {
            console.warn('[Adapter] clearListeners called without eventName!.');
            //return;
        }
        this._googleApi.maps.event.clearListeners(target, eventName);
    }

    trigger(mapInstance, eventName) {
        this._googleApi.maps.event.trigger(mapInstance, eventName);
    }

    /** ui controls **/
    createControls(domElement, position = 'RIGHT_BOTTOM', type = null){
        if(!this._mapInstance || !domElement) { return; }

        const ctrlPos = resolveControlPosition(this._googleApi, position);
        if(ctrlPos === undefined) {
            console.warn(`Unknown control position: ${position}`);
            return;
        }
        console.debug(`[Adapter] Adding control ${type} at position ${position} `);
        this._mapInstance.controls[ctrlPos].push(domElement);
    }

    /** graphics elements (circle, polygon, etc) **/

    createCircle(options) {
        return new this._googleApi.maps.Circle(options);
    }

    createPolygon(options) {
        return new this._googleApi.maps.Polygon(options);
    }

    /** position utilities **/
    createLatLng(lat, lng) {
        return new this._googleApi.maps.LatLng(lat, lng);
    }

    createLatLngBounds() {
        return new this._googleApi.maps.LatLngBounds();
    }

    getDistance(point1, point2) {
        return this._googleApi.maps.geometry.spherical.computeDistanceBetween(point1, point2);
    }

    /** map actions - i.e. zoom, pan, fitTo (maybe?) **/
    performMapAction(action, payload) {
        switch (action) {
            case 'setZoom':
                if (this._mapInstance) {
                    if(payload.level !== undefined) {
                        this._mapInstance.setZoom(payload.level);
                    } else if(payload.difference !== undefined) {
                        this._mapInstance.setZoom(this._mapInstance.getZoom() + payload.difference);
                    }
                }
                break;

            case 'panTo':
                if (this._mapInstance) {
                    this._mapInstance.panTo(this.createLatLng(payload.lat, payload.lng));
                }
                break;

            case 'addControl':
                this.createControls(payload.element, payload.position || 'RIGHT_BOTTOM', payload.type);
                break;

            case 'getMapTypeConstant': {
                if(!payload?.type) { return; }
                const mapTypeConst = resolveMapType(this._googleApi, payload.type);   // neutraler Key → Provider-Tag (Registry)
                if (mapTypeConst === undefined) { console.warn('Unknown mapTypeId: ', payload.type); return null; }
                return mapTypeConst;
            }
            default:
                console.warn(`Unknown map action: ${action}`);
        }
    }

    fitMapToMarkers(map, markers, zoomIdle = true, padding = 10) {
        if(!map || !markers || !markers.length) { return; }

        const bounds = new this._googleApi.maps.LatLngBounds();

        for(const marker of markers) {
            if(marker.getBounds && typeof marker.getBounds === 'function') {
                bounds.union(marker.getBounds());
            } else if(marker.position) {
                bounds.extend((typeof marker.position === 'function' ? marker.position() : marker.position ));
            }
        }

        // Optionale Erweiterung für Marker mit Circle
        if(markers.circle && typeof markers.circle.getBounds === 'function') {
            bounds.union(markers.circle.getBounds());
        }
        map.fitBounds(bounds, padding);

        if(zoomIdle) {
            this._googleApi.maps.event.addListenerOnce(map, 'idle', () => {
                if(map.getZoom() > 17) { map.setZoom(17); }
            });
        }
    }
}